import { Link } from "react-router-dom";
import {
  Heart, Sparkles, ArrowRight, Users, BookOpen, Building2, ShoppingBag,
  GraduationCap, Crown, Vote, ShieldCheck, BadgeCheck, CalendarHeart,
  Star, MapPin, Phone, Mail,
} from "lucide-react";

const MEMBER_PORTAL_URL = "https://app.flintislamiccenter.com/membership";

const ZONES = [
  {
    title: "Family Zone",
    tag: "For Members",
    desc: "Track prayers, Quran reading, and grow together as a family. Earn badges, build habits, and celebrate milestones.",
    bullets: ["Daily prayer tracker", "Quran reading log", "Family badges & streaks"],
    Icon: Users,
    accent: "from-emerald-700 to-emerald-900",
  },
  {
    title: "Quranic Zone",
    tag: "New",
    desc: "Memorize and master Tajweed with curated video lessons, progress tracking, and milestone certificates.",
    bullets: ["Tajweed video courses", "Memorization tracker", "Progress reports"],
    Icon: BookOpen,
    accent: "from-amber-700 to-amber-900",
  },
  {
    title: "Community Sales",
    tag: "Marketplace",
    desc: "Buy, sell, and share with fellow members. Browse free items, list your own, or find what you need within the FIC family.",
    bullets: ["Members-only listings", "Free items shelf", "Halal & trusted"],
    Icon: ShoppingBag,
    accent: "from-rose-700 to-rose-900",
  },
  {
    title: "Facility Reservation",
    tag: "Priority",
    desc: "Members get priority booking and member discounts on all FIC halls — Granada, Cordoba, Conference Hall, and more.",
    bullets: ["Priority booking", "Member rates", "Easy online inquiry"],
    Icon: Building2,
    accent: "from-sky-700 to-sky-900",
  },
];

const PERKS = [
  { Icon: BadgeCheck, label: "Discounted programs", desc: "Save on weekend school, camps, and youth activities." },
  { Icon: Vote, label: "Voice in decisions", desc: "Voting rights at the annual general meeting (active members)." },
  { Icon: CalendarHeart, label: "Event priority", desc: "First seat at iftars, lectures, and family events." },
  { Icon: GraduationCap, label: "Education access", desc: "Member rates for Genesee Academy & weekend classes." },
  { Icon: ShieldCheck, label: "Member services", desc: "Streamlined access to funeral, marriage & medical clinic services." },
  { Icon: Crown, label: "Honored seat", desc: "Recognition as a builder of the FIC community since 1985." },
];

const STEPS = [
  { n: 1, title: "Visit the Member Portal", desc: "Click the button below to open the official FIC member portal." },
  { n: 2, title: "Create your account", desc: "Register with your name, email, and contact details — only takes a minute." },
  { n: 3, title: "Complete membership form", desc: "Fill in your family info, choose your tier, and submit." },
  { n: 4, title: "Welcome to the family", desc: "You'll receive confirmation and gain instant access to all member zones." },
];

export default function Membership() {
  return (
    <div data-testid="membership-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-sand">
        <div className="absolute inset-0 bg-navy">
          <div className="absolute inset-0 pattern-girih opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-navy-900" />
          <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full bg-emerald-500/5 blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-36 text-cream">
          <div className="max-w-3xl">
            <div className="text-[10px] uppercase tracking-[0.35em] text-gold font-semibold inline-flex items-center gap-2">
              <Sparkles size={12} /> Become an FIC Member
            </div>
            <h1 className="display text-5xl md:text-7xl mt-6 leading-[1.02]">
              Join the family.<br />
              <span className="text-gold">Build the future.</span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-cream/80 max-w-2xl leading-relaxed font-serif">
              FIC members are the heart of our masjid. From Friday prayers to family events, your membership keeps the
              lights on, the Quran flowing, and the next generation rooted in faith — <em className="text-gold">since 1985</em>.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={MEMBER_PORTAL_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="membership-apply-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-gold text-navy px-9 py-4 text-sm font-bold uppercase tracking-[0.2em] hover:bg-gold-600 shadow-2xl shadow-gold/25 transition-all hover:-translate-y-0.5"
              >
                <Heart size={16} /> Start your membership <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/donate"
                className="inline-flex items-center gap-2 rounded-full border-2 border-cream/25 text-cream px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-cream/10 transition-colors"
              >
                Support without joining
              </Link>
            </div>

            <div className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4 text-sm text-cream/65">
              <div className="flex items-center gap-2"><Star size={14} className="text-gold" /> Active since 1985</div>
              <div className="flex items-center gap-2"><Users size={14} className="text-gold" /> 1,200+ families served</div>
              <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-gold" /> 501(c)(3) non-profit</div>
            </div>
          </div>
        </div>
        <svg className="absolute bottom-0 left-0 right-0 w-full h-8 md:h-12 text-cream" preserveAspectRatio="none" viewBox="0 0 1440 60">
          <path fill="currentColor" d="M0,30 C320,60 1120,0 1440,30 L1440,60 L0,60 Z" />
        </svg>
      </section>

      {/* WHY JOIN — Quote-driven */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-cream">
        <div className="max-w-5xl mx-auto text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Why become a member?</div>
          <h2 className="font-serif text-4xl md:text-5xl text-ink mt-4 leading-tight">
            Members aren't just supporters — <br className="hidden md:block" />
            <span className="text-navy italic">they're the masjid itself.</span>
          </h2>
          <p className="mt-6 text-ink/65 text-lg max-w-2xl mx-auto leading-relaxed">
            From the first stone in 1985 to the new gymnasium today, every brick of FIC has been laid by members.
            Joining means you don't just visit — you <strong className="text-navy">belong</strong>, <strong className="text-navy">decide</strong>, and <strong className="text-navy">build</strong> with us.
          </p>
        </div>

        {/* PERKS GRID */}
        <div className="max-w-6xl mx-auto mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PERKS.map((p, i) => (
            <div
              key={i}
              className="group rounded-3xl bg-white border border-sand p-7 hover:border-gold/50 hover:shadow-xl hover:shadow-navy/5 hover:-translate-y-1 transition-all"
              data-testid={`membership-perk-${i}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-gold/15 group-hover:bg-gold/25 flex items-center justify-center text-gold transition-colors">
                <p.Icon size={22} />
              </div>
              <h3 className="font-serif text-xl text-ink mt-4">{p.label}</h3>
              <p className="text-sm text-ink/65 mt-2 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MEMBER ZONES — feature cards */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-navy text-cream relative overflow-hidden">
        <div className="absolute inset-0 pattern-girih opacity-10" />
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Inside the Member Portal</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">Your private FIC ecosystem</h2>
            <p className="text-cream/70 mt-4 leading-relaxed">
              Members unlock dedicated zones in the FIC app — built for spiritual growth, family bonding, and community life.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {ZONES.map((z, i) => (
              <a
                key={i}
                href={MEMBER_PORTAL_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={`membership-zone-${i}`}
                className="group rounded-3xl bg-cream/[0.04] border border-cream/10 hover:border-gold/40 hover:bg-cream/[0.08] p-7 md:p-8 transition-all hover:-translate-y-1 backdrop-blur-sm"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${z.accent} flex items-center justify-center text-cream shadow-lg`}>
                    <z.Icon size={26} />
                  </div>
                  <span className="text-[9px] uppercase tracking-[0.22em] text-gold font-semibold bg-gold/10 px-3 py-1.5 rounded-full border border-gold/30">{z.tag}</span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl mt-6">{z.title}</h3>
                <p className="text-cream/70 mt-3 leading-relaxed">{z.desc}</p>
                <ul className="mt-5 space-y-1.5 text-sm text-cream/60">
                  {z.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-gold" /> {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] font-semibold group-hover:translate-x-1 transition-transform">
                  Explore zone <ArrowRight size={12} />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO JOIN — 4 steps */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Easy as 1-2-3-4</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink mt-4">Join in under 5 minutes</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4 md:gap-6">
            {STEPS.map((s, i) => (
              <div key={i} className="relative" data-testid={`membership-step-${s.n}`}>
                <div className="rounded-3xl bg-white border border-sand p-6 h-full hover:border-gold/40 transition-colors">
                  <div className="font-serif text-5xl text-gold/30 leading-none">{String(s.n).padStart(2, "0")}</div>
                  <h3 className="font-serif text-xl text-ink mt-3">{s.title}</h3>
                  <p className="text-sm text-ink/60 mt-2 leading-relaxed">{s.desc}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 rounded-full bg-gold text-navy items-center justify-center shadow-md z-10">
                    <ArrowRight size={12} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 md:px-10 py-20 md:py-28 relative overflow-hidden bg-gradient-to-br from-navy via-navy-900 to-navy text-cream">
        <div className="absolute inset-0 pattern-girih opacity-15" />
        <div className="absolute -top-20 right-10 w-96 h-96 rounded-full bg-gold/5 blur-3xl" />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-gold/15 border border-gold/30 px-5 py-2 text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">
            <Heart size={12} /> Take the next step
          </div>
          <h2 className="font-serif text-4xl md:text-6xl mt-7 leading-[1.05]">
            Be more than a visitor.<br />
            <span className="text-gold">Be a builder.</span>
          </h2>
          <p className="mt-6 text-cream/75 text-lg max-w-2xl mx-auto leading-relaxed">
            The button below opens the secure FIC member portal. Sign up, complete the form,
            and join the families who've made FIC home for nearly four decades.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 items-center justify-center">
            <a
              href={MEMBER_PORTAL_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="membership-cta-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-gold text-navy px-12 py-5 text-base font-bold uppercase tracking-[0.18em] hover:bg-gold-600 shadow-2xl shadow-gold/30 transition-all hover:-translate-y-1"
            >
              <Heart size={18} /> Apply now <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={MEMBER_PORTAL_URL}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-cream/70 hover:text-gold underline-offset-4 hover:underline"
            >
              app.flintislamiccenter.com/membership
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section className="bg-cream border-t border-sand px-6 md:px-10 py-12">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6 text-sm text-ink/75">
          <div className="flex gap-3">
            <MapPin className="text-gold shrink-0" size={18} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">Visit us</div>
              <div className="mt-1 leading-relaxed">9447 Corunna Rd<br />Swartz Creek, MI 48473</div>
            </div>
          </div>
          <div className="flex gap-3">
            <Phone className="text-gold shrink-0" size={18} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">Call us</div>
              <a href="tel:+18104062617" className="mt-1 block hover:text-navy">(810) 406-2617</a>
            </div>
          </div>
          <div className="flex gap-3">
            <Mail className="text-gold shrink-0" size={18} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">Email</div>
              <a href="mailto:flintislamiccenter@gmail.com" className="mt-1 block hover:text-navy break-all">flintislamiccenter@gmail.com</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
